import { useState } from 'react';
import { Box, Button, MenuItem, TextField } from '@mui/material';
import { LinkSection, isValidUrl } from './types';

export interface LinkFormValues {
  label: string;
  to: string;
  sectionId: string;
}

interface LinkFormProps {
  initial: LinkFormValues;
  // Omitted when adding, since a new link goes into the section it was added from.
  sections?: LinkSection[];
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (values: LinkFormValues) => void;
}

const LinkForm = ({ initial, sections, submitLabel, onCancel, onSubmit }: LinkFormProps) => {
  const [values, setValues] = useState(initial);
  const [touched, setTouched] = useState(false);

  const labelError = touched && !values.label.trim();
  const urlError = touched && !isValidUrl(values.to);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!values.label.trim() || !isValidUrl(values.to)) return;
    onSubmit({ ...values, label: values.label.trim(), to: values.to.trim() });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      onKeyDown={(e) => e.key === 'Escape' && onCancel()}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        p: 1.5,
        my: 0.5,
        borderRadius: 2,
        bgcolor: 'action.hover',
      }}
    >
      <TextField
        size="small"
        label="Label"
        autoFocus
        value={values.label}
        onChange={(e) => setValues({ ...values, label: e.target.value })}
        error={labelError}
        helperText={labelError ? 'Label is required' : undefined}
      />
      <TextField
        size="small"
        label="URL"
        placeholder="https://"
        value={values.to}
        onChange={(e) => setValues({ ...values, to: e.target.value })}
        error={urlError}
        helperText={urlError ? 'Must start with http:// or https://' : undefined}
      />
      {sections && (
        <TextField
          select
          size="small"
          label="Section"
          value={values.sectionId}
          onChange={(e) => setValues({ ...values, sectionId: e.target.value })}
          sx={{ '& .MuiSelect-select': { textTransform: 'capitalize' } }}
        >
          {sections.map((section) => (
            <MenuItem key={section.id} value={section.id} sx={{ textTransform: 'capitalize' }}>
              {section.title}
            </MenuItem>
          ))}
        </TextField>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="small" type="submit" variant="text" color="secondary">
          {submitLabel}
        </Button>
      </Box>
    </Box>
  );
};

export default LinkForm;
